import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  ActivityIndicator,
  TouchableOpacity,
  Linking,
  Platform,
} from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { Ionicons } from '@expo/vector-icons';
import { getRestauranteByCodigoQr, type Restaurante } from '../../database/database';
import { getImageDisplayUrl } from '../../lib/utils/imageTransform';
import {
  fetchParticipacionEdicionActivaRestaurante,
  type ParticipacionEdicionActiva,
} from '../../lib/services/restauranteParticipacionEdicion';
import { RestauranteEdicionActivaParticipacionCard } from '../RestauranteEdicionActivaParticipacionCard';
import { DynamicTitle } from '../DynamicTitle';
import { marcarVotarDesdeAppShell } from '../../lib/catalogoEdicionFichaStorage';
import { COMENSAL_TAB_BAR_OVERLAY_INSET } from './ComensalBottomTabBar';
import { getCurrentUserPosition, type UserGeoCoords } from '../../lib/utils/userGeolocation';

type GeoDebugState =
  | { status: 'loading' }
  | { status: 'ok'; coords: UserGeoCoords }
  | { status: 'error'; message: string };

function openUrl(url: string) {
  const t = url.trim();
  if (!t) return;
  Linking.openURL(t.startsWith('http') ? t.replace(/^http:/, 'https:') : `https://${t}`);
}

export function ComensalRestaurantePorQrPage() {
  const { codigoQr } = useParams<{ codigoQr: string }>();
  const navigate = useNavigate();
  const decoded = codigoQr ? decodeURIComponent(codigoQr) : '';
  const [data, setData] = useState<Restaurante | null | undefined>(undefined);
  const [participacion, setParticipacion] = useState<ParticipacionEdicionActiva | null>(null);
  const [participacionLoading, setParticipacionLoading] = useState(false);
  const [geoDebug, setGeoDebug] = useState<GeoDebugState | null>(null);

  useEffect(() => {
    if (!decoded) {
      setGeoDebug(null);
      return;
    }
    let cancel = false;
    setGeoDebug({ status: 'loading' });
    getCurrentUserPosition()
      .then((coords) => {
        if (!cancel) setGeoDebug({ status: 'ok', coords });
      })
      .catch((e) => {
        if (!cancel) {
          setGeoDebug({
            status: 'error',
            message: e instanceof Error ? e.message : 'No se pudo obtener la ubicación.',
          });
        }
      });
    return () => {
      cancel = true;
    };
  }, [decoded]);

  useEffect(() => {
    if (!decoded) {
      setData(null);
      return;
    }
    let cancel = false;
    getRestauranteByCodigoQr(decoded).then((r) => {
      if (!cancel) setData(r);
    });
    return () => {
      cancel = true;
    };
  }, [decoded]);

  useEffect(() => {
    if (!data?.id) {
      setParticipacion(null);
      setParticipacionLoading(false);
      return;
    }
    let cancel = false;
    setParticipacionLoading(true);
    setParticipacion(null);
    fetchParticipacionEdicionActivaRestaurante(data.id)
      .then((p) => {
        if (!cancel) {
          setParticipacion(p);
          setParticipacionLoading(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          setParticipacion(null);
          setParticipacionLoading(false);
        }
      });
    return () => {
      cancel = true;
    };
  }, [data?.id]);

  const title =
    data && (data.nombre_mostrar || data.nombre_oficial)
      ? `${data.nombre_mostrar || data.nombre_oficial} — As Gastronómico`
      : 'Restaurante — As Gastronómico';

  if (data === undefined) {
    return (
      <View style={styles.centered}>
        <DynamicTitle title={title} />
        <ActivityIndicator size="large" color="#030213" />
        <Text style={styles.muted}>Cargando…</Text>
      </View>
    );
  }

  if (!data) {
    return (
      <View style={styles.centered}>
        <DynamicTitle title="Restaurante — As Gastronómico" />
        <Ionicons name="restaurant-outline" size={48} color="#9ca3af" />
        <Text style={styles.errorTitle}>Restaurante no encontrado</Text>
        <Text style={styles.muted}>Este código QR no corresponde a un restaurante activo.</Text>
        <TouchableOpacity style={styles.secondaryBtn} onPress={() => navigate('/')} activeOpacity={0.88}>
          <Text style={styles.secondaryBtnText}>Volver al inicio</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const nombre = data.nombre_mostrar || data.nombre_oficial || 'Restaurante';
  const logoRaw = data.logo || data.logos?.[data.logo_seleccionado_index ?? 0]?.url;
  const logoUri = logoRaw
    ? getImageDisplayUrl(logoRaw, { preset: 'card' }) ?? logoRaw.replace(/^http:/, 'https:')
    : null;
  const instagram = data.instagram?.trim();

  return (
    <View style={styles.shell}>
      <DynamicTitle title={title} />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={{ paddingBottom: COMENSAL_TAB_BAR_OVERLAY_INSET + 24 }}
      >
        <TouchableOpacity style={styles.backRow} onPress={() => navigate(-1)} activeOpacity={0.7}>
          <Ionicons name="chevron-back" size={22} color="#030213" />
          <Text style={styles.backText}>Volver</Text>
        </TouchableOpacity>

        <View style={styles.card}>
          {logoUri ? (
            <Image source={{ uri: logoUri }} style={styles.logo} resizeMode="contain" />
          ) : (
            <View style={[styles.logo, styles.logoPlaceholder]}>
              <Ionicons name="restaurant" size={40} color="#9ca3af" />
            </View>
          )}
          <Text style={styles.title}>{nombre}</Text>
          {data.ciudad_nombre ? <Text style={styles.subtitle}>{data.ciudad_nombre}</Text> : null}
          {data.breve_resena ? <Text style={styles.resena}>{data.breve_resena}</Text> : null}
          {data.sede_ubicacion_corta ? (
            <View style={styles.row}>
              <Ionicons name="location-outline" size={18} color="#6b7280" />
              <Text style={styles.rowText}>{data.sede_ubicacion_corta}</Text>
            </View>
          ) : null}

          {instagram ? (
            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={() => openUrl(instagram)}
              activeOpacity={0.88}
            >
              <Ionicons name="logo-instagram" size={20} color="#ffffff" />
              <Text style={styles.primaryBtnText}>Instagram</Text>
            </TouchableOpacity>
          ) : null}
        </View>

        <View style={styles.participacionWrap}>
          <RestauranteEdicionActivaParticipacionCard
            loading={participacionLoading}
            data={participacion}
            onVotar={
              participacion?.participa && participacion.propuestaIdVotacion
                ? () => {
                    marcarVotarDesdeAppShell();
                    navigate(`/votar/${participacion.propuestaIdVotacion}`);
                  }
                : undefined
            }
          />
        </View>

        {geoDebug ? (
          <View style={styles.geoDebugWrap}>
            {geoDebug.status === 'loading' ? (
              <Text style={styles.geoDebugText}>Obteniendo tu ubicación…</Text>
            ) : geoDebug.status === 'ok' ? (
              <Text style={styles.geoDebugText}>
                Ubicación (prueba): {geoDebug.coords.latitude.toFixed(6)},{' '}
                {geoDebug.coords.longitude.toFixed(6)}
                {geoDebug.coords.accuracy != null
                  ? ` · precisión ~${Math.round(geoDebug.coords.accuracy)} m`
                  : ''}
              </Text>
            ) : (
              <Text style={styles.geoDebugText}>Ubicación no disponible: {geoDebug.message}</Text>
            )}
          </View>
        ) : null}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  scroll: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    backgroundColor: '#f9fafb',
    gap: 12,
  },
  backRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 12,
    gap: 4,
  },
  backText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#030213',
  },
  participacionWrap: {
    paddingHorizontal: 20,
    marginTop: 4,
  },
  geoDebugWrap: {
    marginHorizontal: 20,
    marginTop: 16,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  geoDebugText: {
    fontSize: 11,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 16,
    ...Platform.select({
      web: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' },
      default: { fontFamily: 'monospace' },
    }),
  },
  card: {
    marginHorizontal: 20,
    padding: 24,
    backgroundColor: '#ffffff',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    alignItems: 'center',
    gap: 12,
  },
  logo: {
    width: 112,
    height: 112,
    borderRadius: 12,
    marginBottom: 4,
  },
  logoPlaceholder: {
    backgroundColor: '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#030213',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
  resena: {
    fontSize: 15,
    color: '#374151',
    lineHeight: 22,
    textAlign: 'center',
    marginTop: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 8,
    paddingHorizontal: 4,
  },
  rowText: {
    flex: 1,
    fontSize: 14,
    color: '#4b5563',
    lineHeight: 20,
  },
  primaryBtn: {
    marginTop: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#030213',
    paddingVertical: 12,
    paddingHorizontal: 22,
    borderRadius: 12,
  },
  primaryBtnText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '600',
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#030213',
    textAlign: 'center',
  },
  muted: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    maxWidth: 300,
  },
  secondaryBtn: {
    marginTop: 16,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  secondaryBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#030213',
  },
});
